import { useState, useEffect } from 'react';
import type { NavbarLogo } from '../../schemas/validation';
import type { LogoMode } from './types';
import { DEFAULT_LOGO_DATA } from './types';
import { prepareLogoForEditor } from '../../utils/navbarLogo';

interface UseLogoDataReturn {
  logoData: Partial<NavbarLogo>;
  activeTab: LogoMode;
  setActiveTab: (tab: LogoMode) => void;
  updateLogoData: (updates: Partial<NavbarLogo>) => void;
  clearImage: () => void;
  hasImage: boolean;
}

export function useLogoData(initialData?: Partial<NavbarLogo>): UseLogoDataReturn {
  const prepared = prepareLogoForEditor(initialData);
  const [activeTab, setActiveTab] = useState<LogoMode>(prepared.activeTab);
  const [logoData, setLogoData] = useState<Partial<NavbarLogo>>({
    ...DEFAULT_LOGO_DATA,
    ...prepared.logoData,
    mode: prepared.activeTab,
  });

  useEffect(() => {
    const next = prepareLogoForEditor(initialData);
    setActiveTab(next.activeTab);
    setLogoData({
      ...DEFAULT_LOGO_DATA,
      ...next.logoData,
      mode: next.activeTab,
    });
  }, [initialData]);

  useEffect(() => {
    setLogoData(prev => ({ ...prev, mode: activeTab }));
  }, [activeTab]);

  const updateLogoData = (updates: Partial<NavbarLogo>) => {
    setLogoData(prev => ({ ...prev, ...updates }));
  };

  const clearImage = () => {
    setLogoData(prev => ({
      ...prev,
      imageUrl: undefined,
      cloudfrontUrl: undefined,
      imageAlt: undefined,
      mediaId: undefined,
      s3Key: undefined,
    }));
  };

  const hasImage = !!(logoData.imageUrl || logoData.cloudfrontUrl);

  return {
    logoData,
    activeTab,
    setActiveTab,
    updateLogoData,
    clearImage,
    hasImage,
  };
}
